// DEV-9883 — checkout exposure ordering shim.
//
// PostHog experiments only count metric events with `timestamp >=` the
// person's FIRST `$feature_flag_called` exposure, and both live LP
// experiments use the checkout `$pageview` as their primary-metric
// denominator. The existing exposure paths (Vv5ExposureBeacon,
// TrackingLoader's vv10 effect) run in effects that fire AFTER the
// `$pageview` capture in StoreTrackingScripts — measured at ~4 ms late in
// 65% of vv10 sessions — so the exposure-pageload's checkout pageview is
// excluded from the denominator.
//
// This helper is called synchronously right before the `$pageview` capture.
// Both calls enqueue into the same FIFO queue in scripts/posthog.tsx, so the
// exposure timestamp is <= the pageview timestamp regardless of PostHog init
// state. It fires ONLY the PostHog pair (super property + exposure event) —
// no `split_test` / trackAction — so datalake/GA/Bloomreach are untouched.
// Duplicate `$feature_flag_called` from the existing paths is harmless:
// PostHog attributes on the first exposure per person and ignores
// same-variant repeats.

import Cookies from "js-cookie"
import { STAGE } from "@local/utils/src/services"
import { registerPostHogProperties, trackPostHog } from "../scripts/posthog"
import {
  FREE_SHIPPING_LP_CHANNEL,
  isValidVv5Variant,
  VV5_COOKIE,
  VV5_FEATURE_FLAG,
} from "./freeShipping"
import {
  isVv10Variant,
  VV10_COOKIE,
  VV10_FEATURE_FLAG,
} from "./skuPricePremium"

// Once per (flag, variant) per pageload — skips SPA-nav re-fires, while a
// QA override that changes the cookie mid-session still re-fires with the
// new variant.
const firedCheckoutExposures = new Set<string>()

const fireExposurePair = (
  flag: string,
  variant: string,
  extraSuperProps?: Record<string, unknown>
) => {
  const key = `${flag}:${variant}`
  if (firedCheckoutExposures.has(key)) {
    return
  }
  firedCheckoutExposures.add(key)

  // Register first so the `$pageview` (and everything after) carries the
  // `$feature/<flag>` property, then capture the exposure event itself.
  registerPostHogProperties({
    [`$feature/${flag}`]: variant,
    ...extraSuperProps,
  })
  trackPostHog("$feature_flag_called", {
    $feature_flag: flag,
    $feature_flag_response: variant,
  })
}

/**
 * Fire experiment exposure for LP checkout pageloads, to be called
 * immediately BEFORE the `$pageview` capture so the pageview counts in
 * exposure-gated experiment metrics.
 *
 * @param pathname - The current `window.location.pathname`
 */
const fireCheckoutExposure = (pathname: string) => {
  if (typeof window === "undefined") {
    return
  }

  // Exactly /{market}/lp/{channel}/checkout — a length check so catch-all
  // 404s under /checkout/* can't emit an exposure.
  const segments = pathname.split("/").filter(Boolean)
  const [, section, channel, subpage] = segments
  if (segments.length !== 4 || section !== "lp" || subpage !== "checkout") {
    return
  }

  // [VV5-TEARDOWN] lp-free-shipping (DEV-9829) — remove this block at
  // teardown.
  if (channel === FREE_SHIPPING_LP_CHANNEL) {
    const vv5 = Cookies.get(VV5_COOKIE)
    // A VALID `?vv5=` override marks a QA session — don't enroll it. Only
    // off-prd though: the middleware ignores the override on production
    // (`middleware/freeShipping.ts` STAGE gate), so a prd visitor carrying
    // `?vv5=` is still a real cookie-bucketed participant and MUST count.
    // Bogus override values fall through to a real bucket and count too.
    const override =
      STAGE !== "prd"
        ? new URLSearchParams(window.location.search).get(VV5_COOKIE)
        : null
    if (isValidVv5Variant(vv5) && !isValidVv5Variant(override)) {
      fireExposurePair(VV5_FEATURE_FLAG, vv5)
    }
  }

  // vv10 lp-sku-pricing-modifiers (DEV-9783) — remove at teardown. The bare
  // `vv10` super property mirrors TrackingLoader's registration for ad-hoc
  // breakdowns.
  if (channel === "backpack-hoodie") {
    const vv10 = Cookies.get(VV10_COOKIE)
    if (isVv10Variant(vv10)) {
      fireExposurePair(VV10_FEATURE_FLAG, vv10, { vv10 })
    }
  }
}

export { fireCheckoutExposure }
